import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import logo from "../assets/logo/GoPaperlessAgentMobile.svg";
import logotext from "../assets/logo/GoPaperless Agent.svg";
import searchicon from "../assets/icons/Search.svg";
import arrowdown from "../assets/icons/arrow-down.svg";
import downloadicon from "../assets/icons/downloadicon.svg";
import VNFlag from "../assets/flag-icon/vn.png";
import UKFlag from "../assets/flag-icon/uk.png";
import { Menu } from "lucide-react";
import winIcon from "../assets/icons/win.svg";
import appleIcon from "../assets/icons/Apple.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { stripLang, withLang } from "../utils/localePath";
import {
  isFunctionalCookieAllowed,
  setAccessLangCookie,
} from "../utils/langCookie";
// setAccessLangCookie tự đọc lại functional mỗi lần gọi (hỗ trợ cross-tab)

const Header = ({ onMenuClick }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [openLang, setOpenLang] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const langRef = useRef(null);
  const [openDownload, setOpenDownload] = useState(false);
  const downloadRef = useRef(null);
  const desktopDownloadRef = useRef(null);
  const mobileDownloadRef = useRef(null);

  const currentLang = (i18n.language || "").slice(0, 2);
  const isVI = currentLang === "vi";
  const flagSrc = isVI ? VNFlag : UKFlag;
  const homePath = withLang("/", currentLang === "vi" ? "vi" : "en");

  const switchLang = (lang) => {
    if (lang === currentLang) {
      setOpenLang(false);
      return;
    }

    // Chỉ lưu cookie khi functional === true; nếu Tab khác đã tắt toggle → không ghi
    setAccessLangCookie(lang);

    const scrollY = window.scrollY;
    i18n.changeLanguage(lang);

    // Chỉ đổi prefix locale trên URL — không remount Router, giữ vị trí cuộn
    const rest = stripLang(location.pathname);
    navigate(withLang(rest, lang), {
      replace: true,
      preventScrollReset: true,
    });

    // Phòng trường hợp router vẫn reset scroll
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    });

    setOpenLang(false);
  };


  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setOpenLang(false);
      }
    };

    if (openLang) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openLang]);

  useEffect(() => {
    document.title = t("common.pageTitle");
  }, [i18n.language, t]);

  useEffect(() => {
    const handleConsentChange = () => {
      // Bật functional → đồng bộ cookie theo lang đang hiển thị
      // Tắt functional → clear đã chạy trong applyCookiePreferences; không ghi lại
      if (isFunctionalCookieAllowed()) {
        setAccessLangCookie(currentLang === "vi" ? "vi" : "en");
      }
    };

    window.addEventListener("cookie-consent-changed", handleConsentChange);
    return () =>
      window.removeEventListener("cookie-consent-changed", handleConsentChange);
  }, [currentLang]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isOutsideDesktop =
        desktopDownloadRef.current &&
        !desktopDownloadRef.current.contains(e.target);

      const isOutsideMobile =
        mobileDownloadRef.current &&
        !mobileDownloadRef.current.contains(e.target);

      if (isOutsideDesktop && isOutsideMobile) {
        setOpenDownload(false);
      }
    };

    if (openDownload) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [openDownload]);

  const handleDownloadWindows = () => {
    const url = process.env.REACT_APP_WINDOW_DOWNLOAD_URL;

    if (url) {
      window.location.href = url;
    } else {
      alert("Link download Windows chưa được cấu hình!");
    }
    setOpenDownload(false);
  };

  return (
    <header
      ref={langRef}
      className="fixed top-0 inset-x-0 z-50 bg-[#346DDB] h-[56px] tabletpro:h-[79px] desktop:h-[79px] tabletpro:px-5 tabletair:px-7 tabletmini:px-4 mobile:px-4"
    >
      {/* TABLETPRO + DESKTOP */}
      <div className="hidden desktop:flex max-w-[1400px] h-full mx-auto items-center justify-between px-4">
        {/* Left */}
        <Link to={homePath}>
          <div className="flex items-center justify-center gap-2 cursor-pointer pointer-auto">
            <div>
              <img src={logo} alt="logo" className="w-9 h-10 " />
            </div>

            <div>
              <img src={logotext} alt="logo" className="w-25 h-25 mt-1" />
            </div>
          </div>
        </Link>

        {/* Right */}
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative w-[260px]">
            <input
              type="text"
              placeholder={t("header.search")}
              className="w-full h-[51px] rounded-md bg-transparent placeholder-[#E2E8F0] text-[#E2E8F0] pl-10 pr-1 border border-[#E2E8F0] focus:outline-none items-center"
            />
            <img
              src={searchicon}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
              alt="search"
            />
          </div>

          <div className="relative" ref={desktopDownloadRef}>
            <button
              onClick={() => setOpenDownload(!openDownload)}
              className="px-4 py-2 bg-[#0087FE] h-[51px] text-white rounded-md text-[18px] font-normal flex gap-1 items-center"
            >
              <img src={downloadicon} alt="Download" className="w-6 h-6" />
              {t("header.downloadApp")}
            </button>

            {openDownload && (
              <div className="absolute right-0 mt-2 w-[150px] rounded-[8px] bg-white dark:bg-neutral-10 shadow-md z-[9999]">

                {/* Windows */}
                <button
                  onClick={handleDownloadWindows}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-black dark:text-white hover:bg-[#346DDB] hover:text-white hover:rounded-t-[8px]"
                >
                  Windows
                </button>

                <div className="h-px bg-black/20" />

                {/* macOS */}
                <button
                  className="w-full flex items-center justify-between px-3 py-2 text-sm text-black dark:text-white hover:bg-[#346DDB] hover:text-white hover:rounded-b-[8px]"
                >
                  macOS
                </button>

              </div>
            )}
          </div>

          {/* Language dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpenLang(!openLang)}
              className="flex items-center gap-1 cursor-pointer"
            >
              <img
                src={flagSrc}
                className="w-6 h-6 rounded-full object-cover"
                alt="flag"
              />
              <img src={arrowdown} className="ml-2 w-3 h-3" alt="arrow" />
            </button>

            {openLang && (
              <div className="absolute right-0 mt-2 w-[140px] rounded-[8px] dark:bg-neutral-10 bg-white shadow-md z-[9999]">
                <button
                  type="button"
                  onClick={() => switchLang("en")}
                  className={`w-full px-3 py-2 text-sm text-left text-black dark:text-white hover:bg-[#346DDB] hover:text-white hover:rounded-t-[8px] `}
                >
                  English
                </button>
                <div className="h-px bg-black/20" />
                <button
                  type="button"
                  onClick={() => switchLang("vi")}
                  className={`w-full px-3 py-2 text-sm text-left text-black dark:text-white hover:bg-[#346DDB] hover:text-white hover:rounded-b-[8px] `}
                >
                  Tiếng Việt
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE / TABLET SMALL */}
      <div className="flex desktop:hidden h-full items-cenƯter justify-between px-3">
        {/* Left: menu + logo + name */}
        <div className="flex items-center gap-1 justify-center">
          <button
            onClick={onMenuClick}
            className=" text-white"
          >
            <Menu className="w-6 h-6" />
          </button>

          <Link to={homePath}>
            <div className="flex items-center gap-1">
              <img
                src={logo}
                alt="logo"
                className="w-6 h-7 tabletpro:w-10 tabletpro:h-10 mobile:w-8 mobile:h-9 mini_mobile:h-7 mini_mobile:w-8"
              />
              <img
                src={logotext}
                alt="logo"
                className={`
      w-28 h-28 tabletpro:w-32 tabletpro:h-32 mobile:w-40 mobile:h-40 mt-1 mini_mobile:h-28 mini_mobile:w-36
      ${mobileSearchOpen ? "hidden tabletpro:block tabletmini:block tabletair:block desktop:block" : ""}
    `}
              />
            </div></Link>


        </div>

        {/* Right: search + lang */}
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative flex items-center">
            {!mobileSearchOpen && (
              <button
                className="w-8 h-8 border border-white flex items-center justify-center rounded-sm"
                onClick={() => setMobileSearchOpen(true)}
              >
                <img src={searchicon} alt="search" className="w-4 h-4" />
              </button>
            )}

            {mobileSearchOpen && (
              <div className="flex items-center border border-white rounded-sm h-8 w-40 overflow-hidden bg-transparent transition-all duration-200">
                <div className="relative w-full">
                  <input
                    type="text"
                    autoFocus
                    placeholder={t("header.search")}
                    className="w-full h-8 pl-7 pr-2 text-xs text-white placeholder-white focus:outline-none bg-transparent"
                    onBlur={() => setMobileSearchOpen(false)}
                  />
                  <img
                    src={searchicon}
                    alt="search"
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={mobileDownloadRef}>
            <button
              onClick={() => setOpenDownload(!openDownload)}
              className="w-[35px] h-[35px] bg-[#0087FE] text-white rounded-md flex items-center justify-center"
            >
              <img src={downloadicon} alt="Download" className="w-6 h-6" />
            </button>

            {openDownload && (
              <div className="absolute right-0 mt-2 w-[180px] bg-white dark:bg-neutral-10 rounded-md shadow-md" onClick={(e) => e.stopPropagation()}>
                <button onClick={handleDownloadWindows} className="w-full px-3 py-2 text-left hover:bg-[#346DDB] hover:text-white">
                  Windows
                </button>
                <button className="w-full px-3 py-2 text-left hover:bg-[#346DDB] hover:text-white">
                  macOS
                </button>
              </div>
            )}
          </div>

          {/* Language mobile (dùng chung state openLang) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpenLang(!openLang)}
              className="w-6 h-6 rounded-full border border-white flex items-center justify-center"
            >
              <img
                src={flagSrc}
                alt="lang-flag"
                className="w-full h-full object-cover rounded-full"
              />
            </button>

            {openLang && (
              <div className="absolute right-0 mt-2 w-[140px] rounded-[8px] dark:bg-neutral-10 bg-white shadow-md">
                <button
                  type="button"
                  onClick={() => switchLang("en")}
                  className={`w-full px-3 py-2 text-sm text-left text-black dark:text-white hover:bg-[#346DDB] hover:text-white hover:rounded-t-[8px]`}
                >
                  English
                </button>
                <div className="h-px bg-black/20" />
                <button
                  type="button"
                  onClick={() => switchLang("vi")}
                  className={`w-full px-3 py-2 text-sm text-left text-black dark:text-white hover:bg-[#346DDB] hover:text-white hover:rounded-b-[8px]`}
                >
                  Tiếng Việt
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
